import React, { useEffect, useState } from "react";
import { Row, Col, Button, Flex, Grid } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import CreateSalesOrderForm from "./components/CreateSalesOrderForm";
import SalesOrderTable from "./components/SalesOrderTable";
import OrderDataTableFooter from "./components/OrderDataTableFooter";
import { createSalesOrder, fetchSalesOrderNumber } from "./salesOrderThunk";
import { resetSalesOrder, setFormField } from "./salesOrderSlice";
import Swal from "sweetalert2";
import { getAccessType } from "../../../../lib/functions";
import {
  fetchDiscountAccounts,
  fetchTaxAccounts,
} from "../../../Sales/salesThunks";
import {
  setDiscountAcc,
  setTaxAcc,
  setTransportAcc,
} from "./salesOrderSlice";


const SalesOrderEntry = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { quoteId } = useParams();
  const { edit_sale_o_id } = useParams();
  const screens = Grid.useBreakpoint();

  // 🔹 Bring salesOrder state here
  const {
    itemCart,
    customerInfo,
    footerValues,
    narration,
    sale_o_id,
    voucherNumber,
    payServiceCart,
    selectedDiscountAcc,
    selectedTaxAcc,
    selectedTransportAcc,
    conversion_rate,
    conversion_currency,
    entryDate,
    dueDate,
    tagFieldId,
  } = useSelector((s) => s.salesOrder);

  let [access_type, access_type_set] = useState("");
 
  useEffect(() => {
    const access = getAccessType("sales_order_entry");
    access_type_set(access);
  }, []);

 useEffect(() => {
  dispatch(fetchDiscountAccounts()).then((res) => {
    if (res?.payload) {
      dispatch(setDiscountAcc(res.payload));
    }
  });

  dispatch(fetchTaxAccounts()).then((res) => {
    if (res?.payload) {
      dispatch(setTaxAcc(res.payload));
    }
  });
}, [dispatch]);
  // empty rows removal
  function sanitizeOrderItems(items) {
    return items.filter(row =>
      row.item_id &&
      Number(row.quantity) > 0
    );
  }
  // no duplicate rows of same item
  function validateDuplicateOrderLines(items) {
    const seen = new Set();

    for (const row of items) {
      const key = [
        row.item_id,
        Number(row.rate || 0).toFixed(2),
        Boolean(row.bonus)
      ].join("|");

      if (seen.has(key)) {
        return {
          valid: false,
          message: `Duplicate item detected: ${row.product}`
        };
      }

      seen.add(key);
    }

    return { valid: true };
  }


  // 🔹 Save handler with old validations
  const handleSave = () => {
    // --- Validation checks ---
    if (!customerInfo || !customerInfo.acc_id) {
      Swal.fire("Select customer!", "", "warning");
      return;
    }
    const cleanedItems = sanitizeOrderItems(itemCart);
    if (!itemCart || cleanedItems.length === 0) {
      Swal.fire("Item cart is Empty!", "", "warning");
      return;
    }

    const { valid, message } = validateDuplicateOrderLines(cleanedItems);
    if (!valid) {
      Swal.fire("Duplicate Item", message, "warning");
      return;
    }

    if (narration && narration.length > 255) {
      Swal.fire("Description max size exceeded", "", "warning");
      return;
    }
    if (
      (!customerInfo.contact_no || customerInfo.contact_no.length === 0) &&
      customerInfo.acc_id !== "G"
    ) {
      Swal.fire("Contact No is required", "", "warning");
      return;
    }

     dispatch(
    setFormField({
      field: "itemCart",
      value: cleanedItems,
    })
  );

    // --- Show loading while saving ---
    Swal.fire({
      title: "Saving Sales Order...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    // --- Dispatch save (new UI thunk already builds payload) ---
    dispatch(createSalesOrder())
      .unwrap()
      .then((res) => {
        Swal.close();
        if (!res.error) {

          // 🔥 EDIT MODE
          if ( edit_sale_o_id ) {
            Swal.fire({
              title: "Sales Order Updated Successfully!",
              icon: "success",
            }).then(() => {
              dispatch(resetSalesOrder());
              navigate("/order/sales?tab=entry", { replace: true }); // remove edit id
              dispatch(fetchSalesOrderNumber());
            });
            return;
          }

          Swal.fire({
            title: `Sales Order Created!. Do you want to view invoice?`,
            icon: "success",
            showCancelButton: true,
            confirmButtonText: "Yes, view invoice",
            cancelButtonText: "No, go back",
          }).then((result) => {
            if (result.isConfirmed) {
              dispatch(resetSalesOrder());
              dispatch(fetchSalesOrderNumber());
              navigate(`/order/quick-sales-order-voucher/${res.sale_o_id}`);
            } else {
              dispatch(resetSalesOrder());
              dispatch(fetchSalesOrderNumber());
              navigate("/order/sales");
            }
          });
        } else {
          Swal.fire(res.message || "Something went wrong", "", "warning");
        }
      })
      .catch(() => {
        Swal.close();
        Swal.fire("Network ERROR", "", "error");
      });
  };

  const location = useLocation();

  const handleBack = () => {
    //   if (location.state?.from) {
    //   navigate(location.state.from);
    // } else {
    navigate(-1); // fallback
    // }
  };

  const handleReset = () => {
    dispatch(resetSalesOrder());
    Swal.fire("Form Reset", "All fields and items cleared", "info");
    dispatch(fetchSalesOrderNumber());
  };

  const ActionButtons = () => (
    <Flex justify="flex-end" gap={16} style={{ marginBottom: 16 }}>
      <Button onClick={handleBack}>Back</Button>
      <Button onClick={handleReset}>Reset</Button>
      {access_type == "WRITE" && (
        <>
          <Button
            type="primary"
            onClick={handleSave}
            data-testid="final-save-btn"
          >
            Save
          </Button>
        </>
      )}
    </Flex>
  );

  return (
    <div style={{ padding: screens.xs ? 12 : 20, overflowX: "hidden", width: "100%" }}>
      {/* Top Buttons */}
      <Row>
        <Col span={24}>
          <ActionButtons />
        </Col>
      </Row>

      {/* Form & Table */}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <CreateSalesOrderForm quoteId={quoteId} edit_sale_o_id={edit_sale_o_id} />
        </Col>

        <Col span={24}>
          <SalesOrderTable quoteId={quoteId} edit_sale_o_id={edit_sale_o_id}/>
        </Col>

        {/* <Col span={24}>
          <OrderDataTableFooter />
        </Col> */}
      </Row>

      {/* Bottom Buttons */}
      <Row style={{ marginTop: 24 }}>
        <Col span={24}>
          <ActionButtons />
        </Col>
      </Row>
    </div>
  );
};

export default SalesOrderEntry;
